<!--
  Fills the calendar ahead with AI-suggested draft campaigns.

  A dialog rather than a menu because the two channels fill on genuinely different rules, and the admin should
  see which one they're getting before queueing a few dozen AI generations.
-->
<template>
  <v-dialog
    v-model="dialog"
    max-width="560"
  >
    <v-card>
      <v-card-title class="text-h6">
        Fill the calendar
      </v-card-title>

      <v-card-text>
        <div class="text-caption grey--text mb-2">
          Channel
        </div>
        <v-btn-toggle
          v-model="type"
          mandatory
          dense
          class="mb-4"
        >
          <v-btn :value="$enums.MARKETING.CAMPAIGN_TYPE.EMAIL">
            <v-icon
              small
              left
            >
              mdi-email-outline
            </v-icon>
            Email
          </v-btn>
          <v-btn :value="$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL">
            <v-icon
              small
              left
            >
              mdi-share-variant-outline
            </v-icon>
            Social
          </v-btn>
        </v-btn-toggle>

        <div class="text-caption grey--text mb-2">
          How far ahead
        </div>
        <!-- Weeks as well as months: both channels schedule BY the week (one email broadcast per week, a per-week
             social cap), so "just next week" is a real ask and a month is only ever four of them. -->
        <v-btn-toggle
          v-model="unit"
          mandatory
          dense
          class="mb-4"
        >
          <v-btn value="week">
            Weeks
          </v-btn>
          <v-btn value="month">
            Months
          </v-btn>
        </v-btn-toggle>
        <v-slider
          v-model="count"
          :min="1"
          :max="maxCount"
          :tick-labels="tickLabels"
          ticks="always"
          tick-size="4"
          hide-details
          class="mb-4"
        />

        <!-- Off by default: the usual reason to reach for this is to stock the drafts folder, and dating them
             commits calendar slots to copy nobody has read yet. -->
        <v-checkbox
          v-model="autoSchedule"
          label="Auto schedule"
          hide-details
          dense
          class="mt-0 mb-4"
        />

        <v-alert
          dense
          text
          type="info"
          class="text-caption mb-0"
        >
          {{ explanation }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="confirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// A month is priced at four weeks, not 4.33 — a whole number of weeks keeps "one broadcast per week" exact, and
// under-reaching is better than booking a week the admin didn't ask for. The server applies the same conversion
// to its 24-week ceiling.
const WEEKS_PER_MONTH = 4;

export default {
  name: 'CampaignFillDialog',
  data() {
    return {
      dialog: false,
      type: 1,
      unit: 'month',
      count: 1,
      autoSchedule: false
    };
  },
  computed: {
    isEmail() {
      return this.type === this.$enums.MARKETING.CAMPAIGN_TYPE.EMAIL;
    },
    isWeeks() {
      return this.unit === 'week';
    },
    // Eight weeks covers "the next couple of months" a week at a time; past that the month scale is the easier way
    // to say it, and six months is the server's ceiling either way.
    maxCount() {
      return this.isWeeks ? 8 : 6;
    },
    tickLabels() {
      return Array.from({ length: this.maxCount }, (_, i) => String(i + 1));
    },
    weeks() {
      return this.isWeeks ? this.count : this.count * WEEKS_PER_MONTH;
    },
    amountLabel() {
      const unit = this.isWeeks ? 'week' : 'month';
      return `${this.count} ${unit}${this.count === 1 ? '' : 's'}`;
    },
    // "Fill" only tells the truth when the drafts are dated; undated, the span is a quantity, not a calendar.
    confirmLabel() {
      return this.autoSchedule ? `Fill ${this.amountLabel}` : `Build ${this.amountLabel}`;
    },
    // Spelling out the rule matters: "fill" reads like "fill every slot", and for email it deliberately doesn't.
    explanation() {
      const weeks = this.weeks;
      const span = `the next ${weeks} week${weeks === 1 ? '' : 's'}`;
      const audience = this.isEmail
        ? 'It never creates industry- or topic-targeted emails — those come from audience plans.'
        : 'Posts are public, so these are built around subjects everyone cares about — never one industry ' +
          'or topic.';

      // Auto schedule changes the COUNT, not just the dates: booking the calendar means building only what
      // still fits on it, while undated drafts are a stock and the span is simply how big a stock to make.
      const rule = this.autoSchedule
        ? this.isEmail
          ? `Creates one send-to-everyone email for each of ${span} that doesn't already have one, so every ` +
            'customer hears from us weekly, and books it on the calendar. Weeks already covered are skipped.'
          : `Books one campaign onto every slot still open over ${span}, up to the weekly social cap.`
        : this.isEmail
          ? `Creates one send-to-everyone email per week for ${span} — ${this.weeks} in all — left undated ` +
            'for you to place. What is already on the calendar is not counted against it.'
          : `Creates a full week of posts for each of ${span}, at the weekly social cap, left undated for you ` +
            'to place. What is already on the calendar is not counted against it.';

      return `${rule} ${audience}`;
    }
  },
  watch: {
    // Switching scale keeps the slider in range (6 months, but only 8 weeks) instead of leaving the thumb past
    // the end of its own track.
    maxCount(max) {
      if (this.count > max) this.count = max;
    }
  },
  methods: {
    // Public — the parent opens this through $refs.
    open() {
      this.dialog = true;
    },
    confirm() {
      this.dialog = false;
      // Weeks on the wire: the server fills by week whichever scale was picked here.
      this.$emit('confirm', {
        type: this.type,
        weeks: this.weeks,
        label: this.amountLabel,
        autoSchedule: this.autoSchedule
      });
    }
  }
};
</script>
